import { useCallback, useMemo, useState, type ChangeEvent, type KeyboardEvent } from "react";
import cx from "classnames";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Flex, Text, TextField } from "@radix-ui/themes";
import styles from "./MultiSelectDropdown.module.scss";

export type MultiSelectOption = {
  label: string;
  value: string;
};

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const capitalize = (str: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

export const MultiSelectDropdown = ({
  options,
  value = [],
  onChange,
  placeholder = "Select...",
  disabled = false,
}: MultiSelectDropdownProps) => {
  const [searchValue, setSearchValue] = useState("");

  const isAllSelected = useMemo(() => {
    return (
      value.length === 0 ||
      (options.length > 0 && value.length === options.length)
    );
  }, [value, options]);

  const normalizedSearch = useMemo(
    () => searchValue.trim().toLowerCase(),
    [searchValue]
  );

  const filteredOptions = useMemo(() => {
    if (!normalizedSearch) return options;

    return options.filter((option) => {
      const label = option.label.toLowerCase();
      const optionValue = option.value.toLowerCase();
      return (
        label.includes(normalizedSearch) ||
        optionValue.includes(normalizedSearch)
      );
    });
  }, [options, normalizedSearch]);

  const filteredValues = useMemo(
    () => filteredOptions.map((option) => option.value),
    [filteredOptions]
  );

  const label = useMemo(() => {
    if (isAllSelected) return "All";

    if (value.length === 1) {
      const option = options.find((o) => o.value === value[0]);
      return capitalize(option?.label || value[0]);
    }

    return `${value.length} Selected`;
  }, [value, options, isAllSelected]);

  const isAllResultsSelected = useMemo(() => {
    if (filteredValues.length === 0) return false;
    if (value.length === 0) return true;
    return filteredValues.every((optionValue) => value.includes(optionValue));
  }, [filteredValues, value]);

  const normalizeSelection = useCallback(
    (nextValue: string[]) => {
      if (nextValue.length === 0 || nextValue.length === options.length) {
        return [];
      }
      return nextValue;
    },
    [options.length]
  );

  const handleToggleAllResults = useCallback(() => {
    if (filteredValues.length === 0) return;

    if (value.length === 0) {
      onChange(normalizeSelection(filteredValues));
      return;
    }

    if (filteredValues.every((optionValue) => value.includes(optionValue))) {
      const nextValue = value.filter(
        (optionValue) => !filteredValues.includes(optionValue)
      );
      onChange(normalizeSelection(nextValue));
      return;
    }

    const nextValue = Array.from(
      new Set([...value, ...filteredValues])
    );
    onChange(normalizeSelection(nextValue));
  }, [filteredValues, normalizeSelection, onChange, value]);

  const isOptionSelected = useCallback(
    (optionValue: string) => value.length === 0 || value.includes(optionValue),
    [value]
  );

  const handleToggleOption = useCallback(
    (optionValue: string) => {
      if (value.length === 0) {
        onChange([optionValue]);
        return;
      }

      const newValue = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue];

      onChange(normalizeSelection(newValue));
    },
    [normalizeSelection, onChange, value]
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
    },
    []
  );

  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
    },
    []
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <div
          className={cx(styles.MultiSelectDropdown, {
            [styles.disabled]: disabled,
          })}
        >
          <button className={styles.Trigger} type="button">
            <Flex
              width="100%"
              gap="1"
              align="center"
              className={styles.TriggerContent}
            >
              <Flex className={styles.Value}>
                <Text truncate>{label}</Text>
              </Flex>
              <ChevronDownIcon className={styles.Icon} />
            </Flex>
          </button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={styles.Content} align="start">
        <div className={styles.SearchRow}>
          <TextField.Root
            autoFocus
            placeholder="Search..."
            value={searchValue}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            className={styles.SearchInput}
          >
            <TextField.Slot>
              <MagnifyingGlassIcon className={styles.SearchIcon} />
            </TextField.Slot>
          </TextField.Root>
        </div>

        <div className={styles.Options}>
          <DropdownMenuCheckboxItem
            checked={isAllResultsSelected}
            disabled={filteredOptions.length === 0}
            onSelect={(e) => {
              e.preventDefault();
              handleToggleAllResults();
            }}
          >
            Select All Results
          </DropdownMenuCheckboxItem>

          <DropdownMenuSeparator />

          {filteredOptions.length === 0 ? (
            <DropdownMenuCheckboxItem
              disabled={true}
            >
              No results found
            </DropdownMenuCheckboxItem>
          ) : (
            filteredOptions.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={isOptionSelected(option.value)}
                onSelect={(e) => {
                  e.preventDefault();
                  handleToggleOption(option.value);
                }}
              >
                {capitalize(option.label)}
              </DropdownMenuCheckboxItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
